import React from 'react'
import styles from './Maplet.scss'
import * as L from 'react-leaflet'
import 'react-leaflet-markercluster/dist/styles.css'
import leaflet from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-fullscreen'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import cx from 'classnames'
import {SampleGraph} from 'common/SampleGraph'
import _ from 'lodash'
import * as geoUtils from 'lib/geoUtils'
import {PhotoClusters} from './PhotoClusters'
import {RouteLine} from 'common/Map/RouteLine'
import {Route} from './Route'
import {tileServices} from 'lib/tileServices'
import {MapTypeContext} from 'common/Map/MapTypeContext'
import {RenderBigMenuItem} from 'common/GlobalNav/AdminMenu/Items'
import {getConsole} from 'lib/getConsole'
const console = getConsole('common', 'Maplet')

// todo:
// - maybe write a plugin that lets you pass in accessor values so we don't have
//   to iterate over the trackpoint array each time we want to display it
// - nuke sample graph on unmount!

// pulled from NYC transit colors
const ROUTE_COLORS = [
  '#FC4141',
  '#1DAFE6',
  '#1FAD51',
  '#FCA347',
  '#972F91'
]

const FALLBACK_BOUNDS = leaflet.latLngBounds([[54.572, 33.046], [-18.646, -96.503]])

const Overlay = ({overlaySpec, overwriteMaxZoom}) => {
  switch (overlaySpec.type) {
    case 'arcgis':
      return <L.TileLayer
        key={overlaySpec.id}
        maxZoom={overwriteMaxZoom || overlaySpec.maxZoom}
        minZoom={overlaySpec.minZoom}
        url={`${overlaySpec.url}{z}/{y}/{x}`}
        attribution={overlaySpec.attribution} />
    case 'osm':
      return <L.TileLayer
        key={overlaySpec.id}
        maxZoom={overwriteMaxZoom || overlaySpec.maxZoom}
        minZoom={overlaySpec.minZoom}
        url={`${overlaySpec.url}{z}/{x}/{y}.png${overlaySpec.key ? '?apikey=' + overlaySpec.key : ''}`}
        attribution={overlaySpec.attribution} />
    case 'wms':
      return <L.WMSTileLayer
        key={overlaySpec.id}
        url={overlaySpec.url}
        format='png'
        f='image'
        maxZoom={overwriteMaxZoom || overlaySpec.maxZoom}
        minZoom={overlaySpec.minZoom}
        transparent
        attribution={overlaySpec.attribution} />
    default:
      return null
  }
}

const sumBoundsOptions = (a, b) => {
  const normalizeBounds = x => (x.padding
    ? [[x.padding[0], x.padding[1]], [x.padding[0], x.padding[1]]]
    : [[(x.paddingTopLeft && x.paddingTopLeft[0]) || 0, (x.paddingTopLeft && x.paddingTopLeft[1]) || 0],
      [(x.paddingBottomRight && x.paddingBottomRight[0]) || 0, (x.paddingBottomRight && x.paddingBottomRight[1]) || 0]])
  const aBounds = normalizeBounds(a)
  const bBounds = normalizeBounds(b)
  return {
    paddingTopLeft: [aBounds[0][0] + bBounds[0][0], aBounds[0][1] + bBounds[0][1]],
    paddingBottomRight: [aBounds[1][0] + bBounds[1][0], aBounds[1][1] + bBounds[1][1]]
  }
}

export class Maplet extends React.PureComponent {
  static defaultProps = {
    initialBounds: FALLBACK_BOUNDS,
    zoomPosition: 'bottomright',
    attributionPosition: 'bottomright',
    routes: [],
    zoomOutOnDeselect: true // this defaults to true to preserve legacy behavior, not because I think it's a good idea.
  }

  constructor (props) {
    super(props)
    // should be safe to remove this.state.snap now that <Map /> is handling
    // all of this stuff. will do in a future PR.
    this.state = {
      snap: null,
      mapContext: {mapType: 'leaflet', mapInstance: null}
    }
    this._throttledOnMouseMove = _.throttle(this.onMouseMove, 1000 / 30)
  }

  componentDidMount () {
    const {highlightSlice, zoomToHighlightSlice} = this.props
    highlightSlice && zoomToHighlightSlice && this.zoomToHighlightSlice()
    this.setState({mapContext: {mapType: 'leaflet', mapInstance: this._map}})

    this.updateBounds()
  }

  componentDidUpdate (prevProps) {
    const hasGraph = this.props.sampleGraph && this.props.routes.length === 1
    const hadGraph = prevProps.sampleGraph && prevProps.routes.length === 1
    if (hasGraph !== hadGraph || prevProps.height !== this.props.height) {
      // we've resized vertically, invalidate the leaflet map size
      this.invalidateSize()
    }
    const sliceChanged = this.props.highlightSlice && this.props.highlightSlice !== prevProps.highlightSlice
    const lostSlice = !this.props.highlightSlice && prevProps.highlightSlice
    if (sliceChanged && this.props.zoomToHighlightSlice) {
      this.zoomToHighlightSlice()
    }
    if (lostSlice && this.props.zoomOutOnDeselect) {
      this._map.leafletElement.fitBounds(this.props.routes[0].boundingBox, this.props.boundsOptions)
    }
    if (this.props.suggestedBounds !== prevProps.suggestedBounds) {
      this.updateBounds()
    }
  }

  updateBounds = () => {
    const {suggestedBounds, boundsOptions, routes} = this.props
    if (suggestedBounds) {
      // provide 150px of padding for the SegmentPopup
      this._map.leafletElement.fitBounds(suggestedBounds, sumBoundsOptions(boundsOptions, routes.length > 0 && {paddingTopLeft: [150, 150], paddingBottomRight: [150, 0]}))
    } else if (routes && routes[0]) {
      const bounds = leaflet.latLngBounds(routes[0].boundingBox)
      routes.slice(1).forEach(r => bounds.extend(r.boundingBox))
      this._map.leafletElement.fitBounds(bounds, boundsOptions)
    }
  }

  invalidateSize = () => {
    this._map.leafletElement.invalidateSize()
  }

  onMouseMove = (e) => {
    const {routes, onPointHighlighted} = this.props
    if (!onPointHighlighted) {
      return
    }

    if (routes.length !== 1) {
      if (routes.length === 0) {
        onPointHighlighted(null)
      }
      this.state.snap && this.setState({snap: null})
      return
    }
    const snap = geoUtils.getNearestPointIndex(routes[0], e.latlng, 0.5)

    // short-circuit if there's no snap point found
    if (!snap) {
      onPointHighlighted(null)
      return
    }
    const lat = routes[0].trackPoints[snap].lat
    const lng = routes[0].trackPoints[snap].lng
    const snapPx = this._map.leafletElement.latLngToLayerPoint([lat, lng])
    const distance = e.layerPoint.distanceTo(snapPx)

    // so this is what's causing the problem update....
    this.setState({snap: distance < 50 && snap})
    if (distance < 50) {
      onPointHighlighted({index: snap, routeId: routes[0].id})
    } else {
      onPointHighlighted(null)
    }
  }

  onMoveend = (e) => {
    const {onBoundsChange} = this.props
    onBoundsChange && onBoundsChange(e.target.getBounds())
    this.onZoomCenterChanged()
  }

  zoomToHighlightSlice = () => {
    const {highlightSlice, routes} = this.props
    if (!highlightSlice) {
      return
    }
    this._map.leafletElement.fitBounds(
      leaflet.latLngBounds(routes[0].trackPoints.slice(...highlightSlice).filter(p => p.lat && p.lng))
    )
  }

  onMouseLeave = (e) => {
    const {onPointHighlighted} = this.props
    // the setTimeout is needed to make sure we do this after any throttled
    // mousemove events
    onPointHighlighted && setTimeout(() => onPointHighlighted(null), 1000 / 30 * 2)
  }

  getMapZoom () {
    // TODO: can I move this ref up, or access it from the regional embed?
    return this._map && this._map.leafletElement.getZoom()
  }

  onZoomChange = () => {
    const {onZoomChange} = this.props
    onZoomChange && onZoomChange(this.getMapZoom())
    this.onZoomCenterChanged()
  }

  onZoomCenterChanged = () => {
    if (this.props.onZoomCenterChanged && this._map) {
      const center = this._map.leafletElement.getCenter()
      this.props.onZoomCenterChanged(this.getMapZoom(), {lat: center.lat, lng: center.lng})
    }
  }

  render () {
    const {
      routes, sampleGraph, showPhotos, overlay, highlightSlice,
      highlightPoint, onPointHighlighted, onSegmentSelected,
      metricUnits, onSegmentUnselected, children, onMapClick, boundsOptions, scrollWheelZoom,
      showPOIs, initialBounds, zoomPosition, attributionPosition, clusterGroup, markers,
      showDistanceMarkers, overwriteMaxZoom, showNativeFullscreen
    } = this.props
    const {mapContext} = this.state
    const hasGraph = sampleGraph && routes && routes.length === 1
    const overlaySpec = tileServices[overlay] || tileServices['rwgps']

    if (highlightSlice && routes && routes.length > 1) {
      console.warn('highlightSlice is not supported when displaying multiple routes')
    }

    const hasValidBoundingBox = route => (
      geoUtils.isValidLatLng(route.boundingBox[0]) && geoUtils.isValidLatLng(route.boundingBox[1])
    )

    const viewport = {}
    if (this.props.bounds) {
      viewport.bounds = this.props.bounds
    } else if (this.props.suggestedBounds) {
      viewport.bounds = this.props.suggestedBounds
    } else if (this.props.lastCenter && this.props.lastZoom) {
      viewport.center = this.props.lastCenter
      viewport.zoom = this.props.lastZoom
    } else if (routes.length > 0 && routes.every(hasValidBoundingBox)) {
      viewport.bounds = routes[0] && leaflet.latLngBounds(routes[0].boundingBox)
      routes.slice(1).forEach(r => viewport.bounds.extend(r.boundingBox))
    } else {
      viewport.bounds = initialBounds
    }

    return (
      <div className={cx(styles.Maplet, hasGraph && styles.hasGraph)}>
        <L.Map className={styles.mapContainer}
          onMouseout={this.onMouseLeave} // Leaflet aliases mouseleave to mouseout, which is dumb (https://github.com/Leaflet/Leaflet/issues/1144#issuecomment-10360946)
          onMouseMove={this._throttledOnMouseMove}
          onMoveend={this.onMoveend}
          onClick={onMapClick}
          onZoomend={this.onZoomChange}
          fullscreenControl={showNativeFullscreen}
          ref={m => { this._map = m }}
          {...viewport}
          boundsOptions={boundsOptions}
          zoomControl={false}
          zoomSnap={1}
          zoomDelta={1}
          keyboard={false}
          scrollWheelZoom={scrollWheelZoom}
          attributionControl={false}>
          <Overlay overlaySpec={overlaySpec} overwriteMaxZoom={overwriteMaxZoom} />
          <MapTypeContext.Provider value={mapContext}>
            {!clusterGroup ? markers : clusterGroup}
            {routes.length === 1 && <Route route={routes[0]} key={routes[0].id} showPOIs={showPOIs} showDistanceMarkers={showDistanceMarkers} />}
            {routes.length > 1 && routes.map(r => <RouteLine route={r} key={r.id} color='#FFF' weight={7} showStartMarker={clusterGroup} showEndMarker={false} showPOIs={false} />)}
            {routes.length > 1 && routes.map((r, i) =>
              <Route route={r} key={r.id}
                color={ROUTE_COLORS[i % ROUTE_COLORS.length]}
                showPOIs={false}
                lineOpts={{weight: 4}}
                showMarkers={false} />
            )}
            {routes.length === 1 && highlightSlice && <RouteLine route={routes[0]}
              color='blue'
              slice={highlightSlice}
              showStartMarker={false}
              showEndMarker={false} />}
            {showPhotos && routes.map(r => <PhotoClusters key={r.id} route={r} />)}
            <L.AttributionControl prefix='' position={attributionPosition} />
            <L.ZoomControl position={zoomPosition} />
            {children}
          </MapTypeContext.Provider>
        </L.Map>
        {hasGraph && <div className={styles.sgContainer} onMouseLeave={this.onMouseLeave}>
          <SampleGraph rawPoints={routes[0].rawPoints}
            metricUnits={metricUnits}
            highlightSlice={highlightSlice}
            highlightPoint={highlightPoint}
            onPointHighlighted={onPointHighlighted}
            onSegmentSelected={onSegmentSelected}
            onSegmentUnselected={onSegmentUnselected} />
        </div>}
        {this._map && <RenderBigMenuItem
          getEast={() => this._map.leafletElement.getBounds().getEast()}
          getWest={() => this._map.leafletElement.getBounds().getWest()}
          getNorth={() => this._map.leafletElement.getBounds().getNorth()}
          getSouth={() => this._map.leafletElement.getBounds().getSouth()} />}
      </div>
    )
  }
}
